/* eslint-disable no-unused-vars */
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import {
	Box,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	IconButton,
	InputAdornment,
} from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IS_SA } from '../../../../config/appConfig';
import { AppConstants } from '../../../../config/appConstants';
import { AppRoutes } from '../../../../constants/appRoutes';
import { FormHelper } from '../../../../helpers/formHelper';
import { buildLocalizeRoute } from '../../../../helpers/languageHelper';
import { checkDuplicateEmail } from '../../../../services/authService';
import { SystemSettingsService } from '../../../../services/systemSettingsService';
import { ArrowIcons } from '../../../../shared/components/common/arrowIcons';
import { PasswordValidationPopover } from '../../../../shared/components/common/passwordValidationPopover';
import { ButtonWrapper } from '../../../../shared/components/formUtilities/useButton';
import { InputWrapper } from '../../../../shared/components/formUtilities/useInput';
import { InternationalPhone } from '../../../../shared/components/formUtilities/useInternationalPhone';
import { getRegistrationSchema } from '../../../../shared/validations/schema';

export const BaseRegistration = ({
	clientType = 'investor' | 'borrower',
	defaultEmail = '',
	loading = false,
	onSubmitForm = (values) => { },
	isSA = IS_SA,
}) => {
	const systemSettingsService = new SystemSettingsService();
	const { t } = useTranslation();
	const [duplicateEmailError, setDuplicateEmailError] = useState('');
	const [passwordType, setPasswordType] = useState('password');
	const [loadingEmail, setLoadingEmail] = useState(false);
	const [showErrorPopover, setShowErrorPopover] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();

	const form = useFormik({
		initialValues: {
			email: defaultEmail ? defaultEmail : '',
			contactNumber: '',
			passwordConfirmation: '',
			password: '',
			termsConditionsForInvestmentsAccepted: clientType === 'investor' && !isSA ? true : false,
			shariahTandCAccepted: clientType === 'investor' || isSA ? true : false,
			nationalId: '',
			utm: {
				utmSource:
					searchParams.get(AppConstants.UTMSource)?.toLowerCase() || '',
				utmMedium:
					searchParams.get(AppConstants.UTMMedium)?.toLowerCase() || '',
				utmCampaign:
					searchParams.get(AppConstants.UTMCampaign)?.toLowerCase() || '',
				utmTerm: searchParams.get(AppConstants.UTMTerm)?.toLowerCase() || '',
				utmContent:
					searchParams.get(AppConstants.UTMContent)?.toLowerCase() || '',
			},
		},
		validationSchema: getRegistrationSchema(isSA),
		onSubmit: (values) => {
			let formData = {
				...values,
				passwordConfirmation: values?.password,
			};
			if (!duplicateEmailError) {
				if (!isSA) {
					delete formData.nationalId;
				}
				onSubmitForm(formData);
			}
		},
		...FormHelper.formikProps(),
	});

	const checkIfEmailValid = (value) => {
		setLoadingEmail(true);
		checkDuplicateEmail(value)
			.then((resp) => {
				setLoadingEmail(false);
				if (resp.data) {
					setDuplicateEmailError('emailTaken');
					form.getFieldHelpers('email').setTouched(true);
				} else {
					setDuplicateEmailError('');
				}
			})
			.catch((err) => {
				setLoadingEmail(false);
			});
		return;
	};

	const togglePassword = () => {
		if (passwordType === 'password') {
			setPasswordType('text');
			return;
		}
		setPasswordType('password');
	};

	const getIsInvestor = () => {
		return clientType === 'investor';
	};

	const getIsBorrower = () => {
		return clientType === 'borrower';
	};

	const settings = () => {
		return systemSettingsService.systemSettings();
	};

	return (
		<FormikProvider value={form}>
			<Box className="flex flex-col space-y-4 md:short:space-y-2">
				<form>
					<fieldset
						data-testid="baseRegFieldset">
						<Box className="grid grid-flow-row gap-4 md:short:gap-2">
							<Box className="flex flex-col">
								<InputWrapper
									onBlur={(e) => {
										let value = e.target.value;
										if (value && value?.includes("@"))
											checkIfEmailValid(e.target.value);
									}}
									onFocus={() => {
										setDuplicateEmailError(null);
									}}
									dataTestID="email"
									errorStateTestID="emailError"
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												{loadingEmail ? (
													<CircularProgress className="!h-[18px] !w-[18px]" />
												) : null}
											</InputAdornment>
										),
									}}
									externalErrorMessage={duplicateEmailError}
									name="email"
									type="email"
									key={'email'}
									label={t('email')}></InputWrapper>
							</Box>
							{isSA ? (
								<InputWrapper
									errorStateTestID="nationalIdError"
									dataTestID="nationalIDTest"
									maxLength={10}
									name="nationalId"
									key={'nationalId'}
									label={t('nationalityId')}></InputWrapper>
							) : null}
							<Box>
								<InternationalPhone
									dataTestID="contactNumber"
									errorStateID="contactNumberError"
									name={'contactNumber'}
									key={'contactNumber'}
									label={'mobileNumber'}></InternationalPhone>
								{isSA && getIsInvestor() ? (
									<Box
										data-testid="ksaContactInfoId"
										className="caption !text-[11px] mt-1">
										{t('KSAcontactNumberInfo')}
									</Box>
								) : null}
							</Box>
							<Box className="relative">
								<PasswordValidationPopover
									controllerValue={form.values.password}
									showErrorPopover={showErrorPopover}
								/>
								<InputWrapper
									errorStateTestID="passwordError"
									onFocus={() => setShowErrorPopover(true)}
									onBlur={() => setShowErrorPopover(false)}
									hideErrorMessage
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													className="!bg-transparent text-primary"
													onClick={togglePassword}>
													{passwordType === 'password' ? (
														<VisibilityOffOutlinedIcon></VisibilityOffOutlinedIcon>
													) : (
														<RemoveRedEyeOutlinedIcon></RemoveRedEyeOutlinedIcon>
													)}
												</IconButton>
											</InputAdornment>
										),
									}}
									name="password"
									dataTestID="password"
									type={passwordType}
									key={'password'}
									label={t('password')}></InputWrapper>
							</Box>
							{getIsInvestor() && !isSA ? null : <FormControlLabel
								className="flex !items-start"
								label={
									<span>
										{getIsBorrower() && !isSA ? '' : t('iAcceptThe')}{' '}
										{getIsBorrower() ? (
											isSA ? (
												<>
													<a
														className="underline-link"
														href={settings()?.generalTermsConditions}
														rel="noreferrer"
														target="_blank">
														{t('termsConditionsShort')}
													</a>
													<span>&nbsp;{t('iAuthFundingSouqBorrower')}</span>
												</>
											) : (
												<span>{t('iAuthFundingSouqBorrowerGlobal')}</span>
											)
										) : (
											<>
												{getIsInvestor() ? (
													<span>
														<a
															rel="noreferrer"
															className="underline-link"
															href={settings()?.investorTermsAndConditions}
															target="_blank">
															{t('termsConditionsInvestors')}
														</a>{' '}
													</span>
												) : (
													<span>
														<a
															className="underline-link"
															href={settings()?.borrowerTermsAndConditions}
															rel="noreferrer"
															target="_blank">
															{t('termsConditionsBorrower')}
														</a>
														,
														<a
															className="mx-1 underline-link"
															href={settings()?.borrowersShariahTAndCs}
															rel="noreferrer"
															target="_blank">
															{t('termsConditionsShariahBorrower')}
														</a>
													</span>
												)}
												&{' '}
												<a
													className="underline-link"
													href={settings()?.privacyPolicy}
													rel="noreferrer"
													target="_blank">
													{t('privacyPolicy')}
												</a>
											</>
										)}
									</span>
								}
								control={
									<Checkbox
										data-testid="termsConditionsForInvestmentsAccepted"
										onChange={(e) => {
											form.setFieldValue(
												'termsConditionsForInvestmentsAccepted',
												e.target.checked
											);
										}}
										aria-label='termsConditionsForInvestmentsAccepted'
										className="!px-[3px] !pb-[3px] !pt-0"
										name={'termsConditionsForInvestmentsAccepted'}
										checked={form.values.termsConditionsForInvestmentsAccepted}
										value={form.values.termsConditionsForInvestmentsAccepted}
									/>
								}
							/>}
							{getIsBorrower() && !isSA ? <FormControlLabel
								className="flex !items-start"
								label={t('shariahTandCAccepted')} control={
									<Checkbox
										data-testid="shariahTandCAccepted"
										onChange={(e) => {
											form.setFieldValue(
												'shariahTandCAccepted',
												e.target.checked
											);
										}}
										aria-label='shariahTandCAccepted'
										className="!px-[3px] !pb-[3px] !pt-0"
										name={'shariahTandCAccepted'}
										checked={form.values.shariahTandCAccepted}
										value={form.values.shariahTandCAccepted}
									/>
								}></FormControlLabel> : null}
							<Box className="flex justify-center my-2 md:short:my-1">
								<ButtonWrapper
									id={getIsInvestor() ? IS_SA ? 'lead_sa' : 'lead_ae' : ''}
									dataTestID="submitBtn"
									disabled={loadingEmail}
									// largeBtn
									loading={loading}
									onClick={() => {
										FormHelper.validateForm(form);
									}}
									size="large"
									classList={"!w-full"}
									label={t('continue')}
									endIcon={<ArrowIcons></ArrowIcons>}></ButtonWrapper>
							</Box>
						</Box>
					</fieldset>
				</form>
				<Box className="text-sm text-center text-[#9d9d9d]">
					{t('alreadyAmember')}{' '}
					<a
						href={buildLocalizeRoute(AppRoutes.Login)}
						className="underline-link">
						{t('signIn')}
					</a>
				</Box>
                {isSA && getIsInvestor() && (
                    <Box className="text-sm text-center text-[#9d9d9d] mt-1">
                    {t('notSaudiQuestion')}{' '}
                    <a
                        href={buildLocalizeRoute(`${AppRoutes.InvestorRegistration}/${AppRoutes.IRegFirstStepInternational}`)}
                        className="underline-link">
                            {t('clickHere')}
                    </a>
                    </Box>
                )}
                </Box>
		</FormikProvider>
	);
};
